/**
 * Centralized error code → UX flag dispatcher.
 *
 * Used by `useChatStream` for both pre-stream HTTP errors (ChatHttpError) and
 * in-stream SSE `error` events. Keeping this as a pure function (outside the
 * hook) means:
 *   - It's unit-testable in isolation
 *   - It doesn't get re-created on every render
 *   - Both error paths produce identical UX flag combinations for the same code
 *
 * Spec: docs/design/01-api-reference.md §5 Chat API — error_code 표 참조.
 */

import type { ChatHttpErrorExtra } from '../api/agent';

/** UX flags the dispatcher attaches to onError(). */
export interface ErrorFlags {
  /** Block further sends until user acknowledges (e.g. 409 GENERATION_IN_PROGRESS). */
  blockSend?: boolean;
  /** Seconds before retry is allowed (server's Retry-After header, e.g. 429 QUOTA_EXCEEDED). */
  retryAfter?: number;
  /** UI should exit edit-resend mode (the target message is no longer editable). */
  cancelEditMode?: boolean;
  /** UI should clear pending file attachments (referenced doc is invalid). */
  clearAttachments?: boolean;
}

/** Caller-supplied error reporter. */
export type ErrorHandler = (detail: string, flags?: ErrorFlags) => void;

/**
 * Map an `error_code` to the right `ErrorFlags` and call the supplied handler.
 *
 * Unknown codes fall through to a generic message with the code suffixed for
 * debug. Add new cases here when the backend introduces new structured codes.
 */
export function dispatchByErrorCode(
  onError: ErrorHandler,
  errorCode: string,
  detail: string,
  extra: ChatHttpErrorExtra & { retryAfter?: number },
): void {
  switch (errorCode) {
    case 'GENERATION_IN_PROGRESS':
      onError(detail, { blockSend: true });
      break;
    case 'QUOTA_EXCEEDED':
      onError(detail, { retryAfter: extra.retryAfter });
      break;
    case 'EDIT_TARGET_NOT_FOUND':
    case 'EDIT_TARGET_NOT_USER':
    case 'MESSAGE_ARCHIVED':
    case 'EDIT_TARGET_NOT_IN_STATE':
      onError(detail, { cancelEditMode: true });
      break;
    case 'DOCUMENT_OWNERSHIP_MISMATCH':
    case 'DOCUMENT_TYPE_NOT_SUPPORTED':
    case 'DOCUMENT_NOT_FOUND':
      onError(detail, { clearAttachments: true });
      break;
    default:
      onError(`${detail} [${errorCode}]`);
  }
}
